import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Empty, Card, InputRef, Modal, Space, message } from "antd";
import JobApplicationFilterMenu from "./JobApplicationFilterMenu";
import { JobApplication } from "../../../../Entities/JobApplication";
import {
  PaginationType,
  JobApplicationStatistics,
  JobApplicationFilters,
} from "../../../../Interface";
import JobApplicationTable from "./JobApplicationTable";
import jobApplicationService, {
  BulkStatusChangeResult,
} from "../../../../Service/job.application.service";
import dayjs from "dayjs";
import { openNotification } from "../../../../Components/Notification";
import { capitalizeFirstLetter } from "../../../../Utils/generic-functions";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import ApplicationStatisticsCards from "./ApplicationStatisticsCards";
import { useApplicationFilters } from "../../../../Hooks/useApplicationFilters";
import JobShareSection from "../../../../Components/JobShareSection";
import useOrganization from "../../../../Hooks/useOrganization";
import { useLoader } from "../../../../Context/LoaderContext";
import emailService from "../../../../Service/email.service";
import { BulkEmailApplicationDto } from "../../../../Dto/BulkEmail.dto";
import { JobApplicationStatus } from "../../../../Entities/JobApplication";

interface JobApplicationListingProps {
  jobPostId: string;
  jobPost: JobDescriptionEntity | null;
  onTotalCountChange?: (count: number) => void;
}

export interface JobApplicationListingRef {
  handleExport: () => Promise<void>;
}

const JobApplicationListing = forwardRef<
  JobApplicationListingRef,
  JobApplicationListingProps
>(({ jobPostId, jobPost, onTotalCountChange }, ref) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { org } = useOrganization();
  const isInitialMount = useRef(true);
  const isUpdatingUrl = useRef(false);
  const isUpdatingFromUrl = useRef(false);
  const filtersRef = useRef<JobApplicationFilters | null>(null);
  const paginationRef = useRef<PaginationType | null>(null);
  const shareInputRef = useRef<InputRef>(null);

  const { setLoading, setLoadingMessage } = useLoader();

  // Parse filters from URL - memoized to avoid recalculation
  const urlFilters = useMemo((): Partial<JobApplicationFilters> => {
    const filters: Partial<JobApplicationFilters> = {};

    const status = searchParams.get("status");
    if (status) filters.status = status as JobApplicationFilters["status"];

    const interviewStatus = searchParams.get("interviewStatus");
    if (interviewStatus)
      filters.interviewStatus =
        interviewStatus as JobApplicationFilters["interviewStatus"];

    const profileMatchStatus = searchParams.get("profileMatchStatus");
    if (profileMatchStatus)
      filters.profileMatchSatus =
        profileMatchStatus as JobApplicationFilters["profileMatchSatus"];

    const roundStatus = searchParams.get("roundStatus");
    if (roundStatus)
      filters.roundStatus = roundStatus as JobApplicationFilters["roundStatus"];

    const search = searchParams.get("search");
    if (search) filters.search = search;

    const sort = searchParams.get("sort");
    if (sort) filters.sort = sort as JobApplicationFilters["sort"];

    const uploadType = searchParams.get("uploadType");
    if (uploadType)
      filters.uploadType = uploadType as JobApplicationFilters["uploadType"];

    const requirementId = searchParams.get("requirementId");
    if (requirementId) filters.requirementId = parseInt(requirementId, 10);

    const requirementMatch = searchParams.get("requirementMatch");
    if (requirementMatch)
      filters.requirementMatch =
        requirementMatch as JobApplicationFilters["requirementMatch"];

    return filters;
  }, [searchParams]);

  // Parse pagination from URL - memoized
  const urlPagination = useMemo((): { page: number; limit: number } => {
    const page = parseInt(searchParams.get("page") || "1", 10);
    const limit = parseInt(searchParams.get("limit") || "10", 10);
    return { page: isNaN(page) ? 1 : page, limit: isNaN(limit) ? 10 : limit };
  }, [searchParams]);

  const {
    filters,
    activeFilter,
    updateFilter,
    handleSegmentClick,
    clearAllFilters,
    removeFilter,
    getActiveFiltersList,
    syncActiveFilterFromFilters,
  } = useApplicationFilters(isInitialMount.current ? urlFilters : undefined);

  const [pagination, setPagination] = useState<PaginationType>(() => ({
    limit: urlPagination.limit,
    page: urlPagination.page,
    totalPages: 0,
    totalRecords: 0,
  }));
  const [jobApplications, setJobApplications] = useState<JobApplication[]>([]);

  // Overall statistics - fetched once without filters (used for chart totals)
  const [overallStatistics, setOverallStatistics] =
    useState<JobApplicationStatistics>({
      totalApplications: 0,
      totalProfileMatchQualified: 0,
      totalProfileMatchUnqualified: 0,
      totalProfileMatchPending: 0,
      totalRejected: 0,
      totalHired: 0,
      totalShortlisted: 0,
      totalAIAssessmentsPending: 0,
      totalAIAssessmentsQualified: 0,
      totalAIAssessmentsPartiallyQualified: 0,
      totalAIAssessmentsUnqualified: 0,
      totalInterviewScheduled: 0,
      totalInterviewCompleted: 0,
      totalSlotSelectionPending: 0,
      uploadTypeStats: {
        company: 0,
        candidate: 0,
        lever: 0,
        other: 0,
      },
    });
  const [overallStatsLoaded, setOverallStatsLoaded] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<JobApplication[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Store current filters and pagination in refs for comparison
  filtersRef.current = filters;
  paginationRef.current = pagination;

  // Update URL with current filters, pagination, and sort
  const updateUrlParams = (
    newFilters: JobApplicationFilters,
    newPagination: PaginationType,
  ) => {
    if (isUpdatingUrl.current) return;

    const params = new URLSearchParams(searchParams);

    // Only update if we're on the applications tab
    const currentTab = params.get("tabIndex") || "details";
    if (currentTab !== "applications") return;

    // Update filter params
    if (newFilters.status) {
      params.set("status", newFilters.status);
    } else {
      params.delete("status");
    }

    if (newFilters.interviewStatus) {
      params.set("interviewStatus", newFilters.interviewStatus);
    } else {
      params.delete("interviewStatus");
    }

    if (newFilters.profileMatchSatus) {
      params.set("profileMatchStatus", newFilters.profileMatchSatus);
    } else {
      params.delete("profileMatchStatus");
    }

    if (newFilters.roundStatus) {
      params.set("roundStatus", newFilters.roundStatus);
    } else {
      params.delete("roundStatus");
    }

    if (newFilters.search) {
      params.set("search", newFilters.search);
    } else {
      params.delete("search");
    }

    if (newFilters.sort) {
      params.set("sort", newFilters.sort);
    } else {
      params.delete("sort");
    }

    if (newFilters.uploadType) {
      params.set("uploadType", newFilters.uploadType);
    } else {
      params.delete("uploadType");
    }

    if (newFilters.requirementId) {
      params.set("requirementId", String(newFilters.requirementId));
    } else {
      params.delete("requirementId");
    }

    if (newFilters.requirementMatch) {
      params.set("requirementMatch", newFilters.requirementMatch);
    } else {
      params.delete("requirementMatch");
    }

    // Update pagination params
    if (newPagination.page > 1) {
      params.set("page", newPagination.page.toString());
    } else {
      params.delete("page");
    }

    if (newPagination.limit !== 10) {
      params.set("limit", newPagination.limit.toString());
    } else {
      params.delete("limit");
    }

    isUpdatingUrl.current = true;
    setSearchParams(params, { replace: true });
    setTimeout(() => {
      isUpdatingUrl.current = false;
    }, 0);
  };

  // Sync filters to URL when they change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (filtersRef.current && paginationRef.current) {
      updateUrlParams(filtersRef.current, paginationRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Sync pagination to URL when it changes
  useEffect(() => {
    if (isInitialMount.current) return;
    if (filtersRef.current && paginationRef.current) {
      updateUrlParams(filtersRef.current, paginationRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit]);

  // Handle URL changes (browser back/forward) - sync state from URL
  useEffect(() => {
    if (isUpdatingUrl.current || isInitialMount.current) return;

    const currentTab = searchParams.get("tabIndex") || "details";
    if (currentTab !== "applications") return;

    // Check if URL filters differ from current filters
    const filterKeys: (keyof JobApplicationFilters)[] = [
      "status",
      "interviewStatus",
      "profileMatchSatus",
      "roundStatus",
      "search",
      "sort",
      "uploadType",
      "requirementId",
      "requirementMatch",
    ];

    let urlFiltersChanged = false;
    isUpdatingFromUrl.current = true;
    filterKeys.forEach((key) => {
      const urlValue = urlFilters[key] ?? null;
      const currentValue = filters[key];
      if (urlValue !== currentValue) {
        urlFiltersChanged = true;
        updateFilter(key, urlValue, "dropdown");
      }
    });

    // Sync activeFilter from URL filters if filters changed
    if (urlFiltersChanged) {
      syncActiveFilterFromFilters(urlFilters);
    }

    // Check if URL pagination differs from current pagination
    const urlPaginationChanged =
      urlPagination.page !== pagination.page ||
      urlPagination.limit !== pagination.limit;

    if (urlPaginationChanged) {
      // Update pagination from URL
      setPagination((prev) => ({
        ...prev,
        page: urlPagination.page,
        limit: urlPagination.limit,
      }));
    }

    // Reset flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isUpdatingFromUrl.current = false;
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle filter updates - reset pagination when filters change (unless updating from URL)
  useEffect(() => {
    if (isUpdatingFromUrl.current) return;
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    // Clear selection when filters change
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [filters]);

  const clearFilters = () => {
    clearAllFilters();
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
    // Clear URL params when clearing filters
    const params = new URLSearchParams(searchParams);
    params.delete("status");
    params.delete("interviewStatus");
    params.delete("profileMatchStatus");
    params.delete("roundStatus");
    params.delete("search");
    params.delete("sort");
    params.delete("uploadType");
    params.delete("requirementId");
    params.delete("requirementMatch");
    params.delete("page");
    params.delete("limit");
    setSearchParams(params, { replace: true });
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize || prev.limit,
    }));
    // Don't clear selection - we want to persist selections across pages
  };

  // Reset overallStatsLoaded when jobPostId changes
  useEffect(() => {
    setOverallStatsLoaded(false);
  }, [jobPostId]);

  // Fetch overall statistics once without filters (for chart totals)
  useEffect(() => {
    const fetchOverallStatistics = async () => {
      // Empty filters - all set to null to get overall stats
      const emptyFilters: JobApplicationFilters = {
        status: null,
        search: null,
        profileMatchSatus: null,
        roundStatus: null,
        interviewStatus: null,
        sort: null,
        uploadType: null,
        requirementId: null,
        requirementMatch: null,
      };
      const response = await jobApplicationService.findAllJobApplications(
        jobPostId,
        { page: 1, limit: 1, totalRecords: 0, totalPages: 0 }, // Minimal pagination, we only need stats
        emptyFilters,
      );
      setOverallStatistics(response.statistics);
      setOverallStatsLoaded(true);
    };

    if (jobPostId && !overallStatsLoaded) {
      fetchOverallStatistics();
    }
  }, [jobPostId, overallStatsLoaded]);

  // Fetch filtered job applications for the table
  useEffect(() => {
    const fetchJobApplications = async () => {
      const response = await jobApplicationService.findAllJobApplications(
        jobPostId,
        pagination,
        filters,
      );
      setJobApplications(response.data);
      setPagination((prev) => ({
        ...prev,
        totalRecords: response.total,
        totalPages: response.totalPages,
      }));
      // Notify parent of total count change
      onTotalCountChange?.(response.total);
    };

    if (jobPostId) {
      fetchJobApplications();
    }
  }, [
    pagination.page,
    pagination.limit,
    filters,
    jobPostId,
    onTotalCountChange,
    refreshKey,
  ]);

  const handleExport = async () => {
    try {
      // If rows are selected, export only those; otherwise export all filtered
      let dataToExport: JobApplication[] = [];

      if (selectedRows.length > 0) {
        // Export only selected rows
        dataToExport = selectedRows;
      } else {
        // Export all filtered applications
        const jobApplications =
          await jobApplicationService.findAllJobApplications(
            jobPostId,
            {
              page: 1,
              limit: 10000,
              totalRecords: 0,
              totalPages: 0,
            },
            filters,
          );
        dataToExport = jobApplications.data || [];
      }

      if (!dataToExport || dataToExport.length === 0) {
        message.error("No job applications found to export");
        return;
      }

      const headers = [
        { key: "createdAt", label: "Applied on" },
        { key: "jobPost", label: "job Post" },
        { key: "user", label: "Candidate Name" },
        { key: "email", label: "Email Address" },
        { key: "profileMatchStatus", label: "Profile Match Status" },
        { key: "profileMatchScore", label: "Profile Match Score" },
        { key: "aiAssessmentStatus", label: "AI Assessment Status" },
        { key: "status", label: "Application Status" },
      ];

      const csvRows: string[] = [];

      csvRows.push(headers.map((h) => h.label).join(","));

      for (const application of dataToExport) {
        const values = headers.map((h) => {
          let val: string;
          switch (h.key) {
            case "createdAt":
              val = dayjs(application.createdAt).format("DD MMM YYYY hh:mm A");
              break;
            case "jobPost":
              val = application.jobPost.title ?? "N/A";
              break;
            case "profileMatchStatus":
              val = application.profileMatchStatus
                ? (application.profileMatchStatus.status?.toUpperCase() ??
                  "N/A")
                : "Resume not uploaded";
              break;
            case "profileMatchScore":
              val = application.profileMatchStatus
                ? (application.profileMatchStatus.score?.toString() ?? "N/A")
                : "N/A";
              break;
            case "user":
              val =
                `${capitalizeFirstLetter(
                  application.user.firstName ?? "",
                )} ${capitalizeFirstLetter(
                  application.user.lastName ?? "",
                )}`.trim() || "N/A";
              break;
            case "email":
              val = application.user.email ?? "N/A";
              break;
            case "aiAssessmentStatus":
              val = application.questionnaireStatus
                ? application.questionnaireStatus.toUpperCase()
                : "N/A";
              break;
            case "status":
              val = application.status?.toUpperCase() ?? "N/A";
              break;
            default:
              val = "N/A";
          }
          return `"${val}"`;
        });
        csvRows.push(values.join(","));
      }

      const csvContent = csvRows.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `job_applications_${
          jobPost ? jobPost.title : jobPostId
        }_${dayjs().format("DD-MMM-YYYY hh:mm A")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Unable to export applications. Please try again.");
      console.error("Export failed:", err);
    }
  };

  useImperativeHandle(ref, () => ({
    handleExport,
  }));

  const activeFiltersList = getActiveFiltersList();

  // Check if there are no applications using jobPost
  const hasNoApplications = jobPost && jobPost.numberOfApplications === 0;

  // Generate job link for sharing
  const jobLink =
    org?.orgDomain && jobPost
      ? `https://${org.orgDomain}/jobs/${jobPost.slug}/requirements`
      : "";

  const handleCopyJobLink = () => {
    if (jobLink) {
      navigator.clipboard.writeText(jobLink);
      message.success("Job post link copied to clipboard");
    }
  };

  const handleNavigateToJobLink = () => {
    if (jobLink) {
      window.open(jobLink, "_blank");
    }
  };

  const handleSendBulkEmail = async () => {
    if (selectedRows.length === 0) {
      message.error(
        "No applications selected. Please select at least one application to send email.",
      );
      return;
    }

    const selectedApplications = selectedRows;

    const pendingProfileMatchApplications: BulkEmailApplicationDto[] =
      selectedApplications
        .filter(
          (application) =>
            application.status === "applied" &&
            !application.profileId &&
            !application.profileMatchStatus,
        )
        .map((application) => {
          return {
            jobApplicationId: application._id,
            jobPost: application.jobPost,
            user: application.user,
            mailType: "profile_match_pending",
          };
        });

    const pendingAssessmentApplications: BulkEmailApplicationDto[] =
      selectedApplications
        .filter(
          (application) =>
            application.status === "applied" &&
            application?.questionnaireStatus &&
            application?.questionnaireStatus === "pending",
        )
        .map((application) => {
          return {
            jobApplicationId: application._id,
            jobPost: application.jobPost,
            user: application.user,
            mailType: "assessment_pending",
          };
        });

    const pendingSlotSelectionApplications: BulkEmailApplicationDto[] =
      selectedApplications
        .filter(
          (application) =>
            application.status === "interview" &&
            !application?.interviewSchedule,
        )
        .map((application) => {
          return {
            jobApplicationId: application._id,
            jobPost: application.jobPost,
            user: application.user,
            mailType: "slot_selection_pending",
          };
        });

    const noProfileMatch = pendingProfileMatchApplications.length === 0;
    const noAssessment = pendingAssessmentApplications.length === 0;
    const noSlotSelection = pendingSlotSelectionApplications.length === 0;

    if (noProfileMatch && noAssessment && noSlotSelection) {
      message.error("Please select applications that require email reminders.");
      return;
    }

    const mailSendingApplicationIds = [
      ...pendingProfileMatchApplications.map(
        (application) => application.jobApplicationId,
      ),
      ...pendingAssessmentApplications.map(
        (application) => application.jobApplicationId,
      ),
      ...pendingSlotSelectionApplications.map(
        (application) => application.jobApplicationId,
      ),
    ];

    const mailNotRequiredEmails: string[] = selectedApplications
      .filter(
        (application) => !mailSendingApplicationIds.includes(application._id),
      )
      .map((application) => application.user?.email)
      .filter((email): email is string => typeof email === "string");

    try {
      setLoading(true);
      setLoadingMessage("Sending email reminders to candidates...");

      const response = await emailService.sendBulkEmail({
        pendingProfileMatchApplications,
        pendingAssessmentApplications,
        pendingSlotSelectionApplications,
      });

      if (response?.status === false) {
        message.error("Failed to send email to candidates. Please try again.");
        return;
      }

      message.success("Email reminders sent successfully to candidates.");

      // Show notifications for candidates who don't need email reminders
      if (mailNotRequiredEmails.length > 0) {
        for (const email of mailNotRequiredEmails) {
          message.error(`Email reminder not required for ${email}`);
        }
      }
      return;
    } catch (error: any) {
      console.error(
        "JobApplicationListing -> handleSendBulkEmail -> Error:",
        error,
      );
      message.error("Failed to send email to candidates. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  };

  const performBulkStatusChange = async (status: JobApplicationStatus) => {
    try {
      setLoading(true);
      setLoadingMessage(`Marking applications as ${status}...`);

      const result: BulkStatusChangeResult =
        await jobApplicationService.bulkUpdateStatus(
          selectedRows.map((r) => r._id),
          status,
        );

      let notificationType: "success" | "warning" | "error" = "success";
      let message = `${result.changed} application(s) marked as ${status}.`;

      if (result.skipped > 0) {
        message += ` ${result.skipped} application(s) were skipped (not eligible or already actioned).`;
        notificationType = result.failed > 0 ? "error" : "warning";
      }

      if (result.failed > 0) {
        message += ` ${result.failed} application(s) failed to update.`;
        notificationType = "error";
      }

      openNotification(notificationType, "Status Updated", message);

      setSelectedRowKeys([]);
      setSelectedRows([]);
      setOverallStatsLoaded(false);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      console.error("handleBulkStatusChange error:", error);
      message.error("Failed to update application statuses. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const getConfirmationContent = (
    status: JobApplicationStatus,
    selectedCount: number,
  ) => {
    let content = null;
    let okButtonText = "Confirm";

    switch (status) {
      case "shortlisted":
        okButtonText = "Mark as Shortlisted";
        content = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Mark {selectedCount} application(s) as shortlisted</li>
              <li>Move the candidates to the shortlisted stage</li>
              <li>Consider the candidates for further interview rounds</li>
              <li>Permanently change the application status</li>
              <li>
                Send email notifications to the candidates about being
                shortlisted
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "rejected":
        okButtonText = "Mark as Rejected";
        content = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Mark {selectedCount} application(s) as rejected</li>
              <li>Move the candidates to the rejected stage</li>
              <li>
                Remove the candidates from consideration for this position
              </li>
              <li>Permanently change the application status</li>
              <li>
                Send email notifications to the candidates about the rejection
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "hired":
        okButtonText = "Mark as Hired";
        content = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Mark {selectedCount} application(s) as hired</li>
              <li>Move the candidates to the hired stage</li>
              <li>
                Indicate the candidates have been selected for this position
              </li>
              <li>Permanently change the application status</li>
              <li>
                Send email notifications to the candidates about being hired
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
    }

    return { content, okButtonText };
  };

  const handleBulkStatusChange = async (status: JobApplicationStatus) => {
    if (selectedRows.length === 0) {
      message.error(
        "No applications selected. Please select at least one application.",
      );
      return;
    }

    // Check vacancy limit for hiring first
    if (
      status === "hired" &&
      jobPost?.numberOfOpenings &&
      jobPost?.numberOfHiredApplications !== undefined
    ) {
      const currentHired = jobPost.numberOfHiredApplications;
      const openings = jobPost.numberOfOpenings;

      if (currentHired >= openings) {
        Modal.confirm({
          title: "Vacancy Limit Reached",
          content: (
            <div>
              <p style={{ marginBottom: "12px", fontWeight: 500 }}>
                You have already hired <strong>{currentHired}</strong>{" "}
                candidate(s) for this position.
              </p>
              <p style={{ marginBottom: "12px", fontWeight: 500 }}>
                The job post has <strong>{openings}</strong> opening(s).
              </p>
              <p>
                Do you want to proceed with hiring {selectedRows.length}{" "}
                candidate(s)?
              </p>
            </div>
          ),
          okText: "Yes, Proceed",
          okType: "primary",
          cancelText: "Cancel",
          centered: true,
          onOk: async () => {
            showConfirmationModal(status);
          },
        });
        return;
      }
    }

    showConfirmationModal(status);
  };

  const showConfirmationModal = (status: JobApplicationStatus) => {
    const { content, okButtonText } = getConfirmationContent(
      status,
      selectedRows.length,
    );

    Modal.confirm({
      title: "Confirm Action",
      content,
      okText: okButtonText,
      cancelText: "Cancel",
      icon: null,
      centered: true,
      okButtonProps: {
        style: {
          backgroundColor:
            org?.theme?.primaryColor || "var(--ant-color-primary)",
          borderColor: org?.theme?.primaryColor || "var(--ant-color-primary)",
        },
      },
      onOk: async () => {
        await performBulkStatusChange(status);
      },
    });
  };

  const handleBulkReportDownload = () => {
    if (selectedRows.length === 0) {
      openNotification(
        "warning",
        "Please select atleast one job application",
        "",
      );
      return;
    }

    const executeDownload = async (maskPersonalDetails: boolean) => {
      try {
        setLoading(true);
        setLoadingMessage("Preparing reports for download...");
        const skipped =
          selectedRows.filter((s) => !s.profileId && !s.profileMatchStatus) ||
          [];

        if (skipped.length === selectedRows.length) {
          openNotification(
            "warning",
            "Reports are not available for the selected applications",
            "",
          );
          return;
        }

        const applicationIds = selectedRows.map((s) => s._id);
        const blob = await jobApplicationService.downloadBulkApplicationReport(
          applicationIds,
          maskPersonalDetails,
        );
        if (blob) {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          const now = new Date();
          const datePart = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
          const titlePart = (jobPost?.title ?? "reports").replace(
            /[^a-zA-Z0-9_-]/g,
            "_",
          );
          link.setAttribute("download", `${titlePart}-reports-${datePart}.zip`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          if (skipped.length > 0) {
            Modal.info({
              title: "Some reports were not available",
              centered: true,
              content: (
                <div>
                  <p style={{ marginBottom: 8 }}>
                    The following candidates were skipped:
                  </p>
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    {skipped.map((s) => (
                      <li key={s._id}>
                        {s.user.firstName} {s.user.lastName} — Report is not
                        available
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            });
          }
          setSelectedRows([]);
          setSelectedRowKeys([]);
        } else {
          openNotification("error", "Failed to download reports", "");
        }
      } catch (error: any) {
        console.error("~ handleBulkReportDownload ~ error:", error);
        openNotification("error", "Failed to download reports", "");
      } finally {
        setLoading(false);
        setLoadingMessage(null);
      }
    };

    const closeRef = { current: () => {} };
    const instance = Modal.confirm({
      title: "Download Reports",
      centered: true,
      icon: null,
      content:
        "Do you want to mask the candidates' personal information (phone & email) in the downloaded reports?",
      closable: true,
      maskClosable: false,
      footer: (
        <Space
          style={{ justifyContent: "flex-end", width: "100%", paddingTop: 8 }}
        >
          <Button
            danger
            onClick={() => {
              closeRef.current();
              executeDownload(false);
            }}
          >
            Download As-Is
          </Button>
          <Button
            type="primary"
            onClick={() => {
              closeRef.current();
              executeDownload(true);
            }}
          >
            Mask &amp; Download
          </Button>
        </Space>
      ),
    });
    closeRef.current = instance.destroy;
  };

  // Empty state when no applications exist
  if (hasNoApplications) {
    return (
      <section className="job-application-listing-section">
        {jobPost && jobPost?.appJobId && jobPost.isActive && (
          <JobApplicationFilterMenu
            filters={filters}
            updateFilter={(key, value) => updateFilter(key, value, "dropdown")}
            clearFilters={clearFilters}
            jobPost={jobPost}
            handleSendBulkEmail={handleSendBulkEmail}
            jobPostActive={jobPost?.isActive || false}
            atsImportOnly={true}
            handleBulkReportDownload={handleBulkReportDownload}
          />
        )}
        <Card variant="borderless" className="no-applications-empty-state">
          <Empty
            image={false}
            description={
              <div style={{ maxWidth: 600, margin: "0 auto" }}>
                <JobShareSection
                  jobLink={jobLink}
                  jobTitle={jobPost?.title || "Job Opening"}
                  hasActiveJobPost={jobPost?.isActive ?? false}
                  inputRef={shareInputRef}
                  onNavigateTo={handleNavigateToJobLink}
                  onCopy={handleCopyJobLink}
                  onPlatformClick={() => {}}
                />
              </div>
            }
          />
        </Card>
      </section>
    );
  }

  return (
    <section className="job-application-listing-section">
      {jobPost && (
        <ApplicationStatisticsCards
          statistics={overallStatistics}
          onSegmentClick={handleSegmentClick}
          activeFilter={activeFilter}
          activeFiltersList={activeFiltersList}
          onRemoveFilter={(key) => removeFilter(key as keyof typeof filters)}
          jobPost={jobPost}
          appliedFilters={filters}
        />
      )}

      {jobPost && (
        <JobApplicationFilterMenu
          filters={filters}
          updateFilter={(key, value) => updateFilter(key, value, "dropdown")}
          clearFilters={clearFilters}
          jobPost={jobPost}
          onExport={handleExport}
          exportDisabled={jobApplications.length === 0}
          selectedCount={selectedRowKeys.length}
          handleSendBulkEmail={handleSendBulkEmail}
          jobPostActive={jobPost?.isActive || false}
          handleBulkStatusChange={handleBulkStatusChange}
          selectedRows={selectedRows}
          handleBulkReportDownload={handleBulkReportDownload}
        />
      )}

      {jobPost && (
        <JobApplicationTable
          jobApplications={jobApplications}
          pagination={pagination}
          tab="applications"
          idInQuery={jobPostId || ""}
          onPaginationChange={handlePaginationChange}
          jobPost={jobPost}
          onSortChange={(sortKey, sortOrder) => {
            if (sortKey) {
              updateFilter("sort", sortKey, "dropdown");
            } else {
              updateFilter("sort", null, "dropdown");
            }
          }}
          selectedRowKeys={selectedRowKeys}
          onSelectChange={(keys, rows) => {
            // Get IDs of applications on the current page
            const currentPageIds = new Set(
              jobApplications.map((app) => app._id),
            );

            // Keep selections from other pages (not on current page)
            const selectionsFromOtherPages = selectedRowKeys.filter(
              (key) => !currentPageIds.has(key),
            );
            const rowsFromOtherPages = selectedRows.filter(
              (row) => !currentPageIds.has(row._id),
            );

            // Merge: other pages' selections + current page's new selections
            setSelectedRowKeys([...selectionsFromOtherPages, ...keys]);
            setSelectedRows([...rowsFromOtherPages, ...rows]);
          }}
          activeFilters={filters}
        />
      )}
    </section>
  );
});

export default JobApplicationListing;
